'use client'

import { useCallback, useMemo, useState } from 'react'
import { CheckCircle, FolderPlus, Link2, Share2, type LucideIcon } from 'lucide-react'
import { useAddFileToDriveMutation } from '@/api/nodes/mutations/add-file-to-drive-mutation'
import { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { useCopyAdShareLink } from '@/api/research-jobs/ads/use-copy-ad-share-link'
import { dispatchAttachmentToActiveTaskTarget, dispatchNewTaskIntent } from '@/features/agents/utils/task-target-dispatcher'
import { useDownloadFiles } from '@/features/files/download-files'
import { buildResearchAdAttachment } from '@/features/research/research-ads/research-ad-card/utils/research-ad-attachment'
import { toast } from '@/features/toast/toast'
import { useOrganization } from '@clerk/nextjs'

/**
 * One overflow-menu / Other Actions row. The card ⋯ menu and the preview modal both render
 * `useResearchAdActions().menu` — add an action here and it appears on both; do not list it in
 * only one surface.
 */
export interface IResearchAdMenuAction {
    key: string
    label: string
    Icon: LucideIcon
    onClick: () => void
    disabled?: boolean
    busy?: boolean
    active?: boolean
}

/**
 * Single source of truth for the research ad's actions, shared by the card's hover overlay and the
 * preview modal (the `use-creative-actions` pattern) — so saved state stays consistent no matter
 * which surface triggered the mutation. `menu` is the shared overflow set (card ⋯ and modal Other
 * Actions); card-only chrome (Select, growth chart) stays on the card.
 */
export function useResearchAdActions(ad: IResearchedAd) {
    const [isSavedToDrive, setIsSavedToDrive] = useState<boolean>(Boolean(ad.savedToDrive))

    const { organization } = useOrganization()
    const addFileToDrive = useAddFileToDriveMutation()

    const { copyShareLink: copyAdShareLink, sharePending } = useCopyAdShareLink()
    const downloadFiles = useDownloadFiles()

    const copyShareLink = useCallback(() => copyAdShareLink(ad.uuid), [copyAdShareLink, ad.uuid])

    const saveToDrive = useCallback(async () => {
        if (!organization) return
        if (!ad.creative.mediaFile?.uuid) return

        const promise = addFileToDrive.mutateAsync({
            name: ad.creative.title ?? 'Untitled Ad',
            fileUuid: ad.creative.mediaFile.uuid,
        })

        toast.promise(promise, {
            loading: 'Saving to drive...',
            success: 'Saved to drive',
            error: 'Failed to save to drive',
        })

        // toast.promise reports the failure but does not consume the rejection — without the catch
        // a failed save becomes an unhandled rejection from the un-awaited onClick.
        try {
            await promise
            setIsSavedToDrive(true)
        } catch {
            // Reported by the toast; the saved flag correctly stays false.
        }
    }, [organization, ad.creative.mediaFile?.uuid, ad.creative.title, addFileToDrive])

    const viewOriginal = useCallback(() => {
        if (ad.creative.snapshotUrl) {
            window.open(ad.creative.snapshotUrl, '_blank')
        }
    }, [ad.creative.snapshotUrl])

    const download = useCallback(() => {
        if (!ad.creative.mediaFile?.url) return
        const fileName = `${ad.creative.title ?? ad.uuid}.${ad.creative.mediaFile.type}`
        downloadFiles({ files: [{ file: ad.creative.mediaFile as never, fileName }] })
    }, [ad.creative.mediaFile, ad.creative.title, ad.uuid, downloadFiles])

    const attachToChat = useCallback(() => {
        dispatchAttachmentToActiveTaskTarget(buildResearchAdAttachment(ad), null, { openNewTask: true })
    }, [ad])

    const mediaCategory = ad.creative.mediaFile?.category

    const clone = useCallback(async () => {
        try {
            dispatchNewTaskIntent({
                attachment: buildResearchAdAttachment(ad),
                prompt:
                    mediaCategory === 'VIDEO'
                        ? 'Recreate this video ad for my brand'
                        : 'Recreate this static ad for my brand',
                autoSubmit: false,
            })
        } catch {
            toast.error('Failed to create chat. Please try again.')
        }
    }, [ad, mediaCategory])

    const savePending = addFileToDrive.isPending
    const canSaveToDrive = Boolean(ad.creative.mediaFile?.uuid)
    const canViewOriginal = Boolean(ad.creative.snapshotUrl)

    const menu: IResearchAdMenuAction[] = useMemo(
        () => [
            {
                key: 'save-to-drive',
                label: isSavedToDrive ? 'Saved to Drive' : 'Save to Drive',
                Icon: isSavedToDrive ? CheckCircle : FolderPlus,
                onClick: () => void saveToDrive(),
                disabled: !canSaveToDrive || isSavedToDrive || savePending,
                busy: savePending,
                active: isSavedToDrive,
            },
            {
                key: 'view-original',
                label: 'View original',
                Icon: Link2,
                onClick: viewOriginal,
                disabled: !canViewOriginal,
            },
            {
                key: 'copy-share-link',
                label: 'Copy share link',
                Icon: Share2,
                onClick: () => void copyShareLink(),
                disabled: sharePending,
                busy: sharePending,
            },
        ],
        [
            isSavedToDrive,
            saveToDrive,
            canSaveToDrive,
            savePending,
            viewOriginal,
            canViewOriginal,
            copyShareLink,
            sharePending,
        ]
    )

    return {
        isSavedToDrive,
        savePending,
        saveToDrive,
        canSaveToDrive,
        viewOriginal,
        canViewOriginal,
        copyShareLink,
        sharePending,
        download,
        canDownload: Boolean(ad.creative.mediaFile?.url),
        attachToChat,
        clone,
        canClone: mediaCategory === 'IMAGE' || mediaCategory === 'VIDEO',
        menu,
    }
}
